/**
 * BOM (Bill of Materials) View
 * Generate BOMs from extractions and reconcile with ERP parts
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Autocomplete,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Tooltip,
  Card,
  CardContent,
  LinearProgress,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  CheckCircle as MatchedIcon,
  Warning as UnmatchedIcon,
  Visibility as ViewIcon,
  Assignment as BOMIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

interface ExtractionForBOM {
  job_id: string;
  pdf_filename: string;
  created_at: string;
  extraction_count: number;
  has_bom: boolean;
  bom_id: number | null;
}

// Helper function to clean up filename (remove hash prefix)
const cleanFilename = (filename: string): string => {
  // Remove hash prefix like "d880caf4a9704cf9a89cbe15fcb5d00b_"
  return filename.replace(/^[a-f0-9]{32}_/i, '');
};

interface BOMSummary {
  id: number;
  name: string;
  extraction_job_id: string;
  pdf_filename: string;
  status: string;
  total_items: number;
  matched_items: number;
  unmatched_items: number;
  created_at: string;
  notes: string | null;
}

interface BOMLineItem {
  id: number;
  ref_des: string | null;
  component_type: string | null;
  extraction_description: string | null;
  erp_part_id?: number | null;
  pp_number: string | null;
  vendor_name: string | null;
  vendor_part_number: string | null;
  description: string | null;
  quantity: number;
  uom: string;
  match_status: string;
  match_confidence: number | null;
  needs_review: boolean;
  page_numbers: string | null;
  notes: string | null;
}

interface ERPPart {
  id: number;
  internal_part_number: string;
  external_reference: string;
  description: string;
  manufacturer_name: string;
}

const BOMView: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Extractions for BOM generation
  const [extractions, setExtractions] = useState<ExtractionForBOM[]>([]);
  
  // Existing BOMs
  const [boms, setBoms] = useState<BOMSummary[]>([]);
  const [selectedBom, setSelectedBom] = useState<BOMSummary | null>(null);
  const [bomItems, setBomItems] = useState<BOMLineItem[]>([]);
  
  // Dialogs
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedExtraction, setSelectedExtraction] = useState<ExtractionForBOM | null>(null);
  const [newBomName, setNewBomName] = useState('');
  const [generating, setGenerating] = useState(false);
  
  // ERP part selection for manual matching
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [erpSearchResults, setErpSearchResults] = useState<ERPPart[]>([]);
  const [erpSearchQuery, setErpSearchQuery] = useState('');

  // Load extractions (deduplicated by filename, keeping only latest)
  const loadExtractions = useCallback(async () => {
    try {
      const response = await fetch('/api/bom/extractions');
      const data = await response.json();
      if (data.success) {
        // Deduplicate by cleaned filename, keeping only the latest extraction for each file
        const fileMap = new Map<string, ExtractionForBOM>();
        for (const ext of data.extractions) {
          const cleanName = cleanFilename(ext.pdf_filename);
          // Since extractions are sorted by created_at desc, first one is the latest
          if (!fileMap.has(cleanName)) {
            fileMap.set(cleanName, ext);
          }
        }
        setExtractions(Array.from(fileMap.values()));
      }
    } catch (error) {
      console.error('Failed to load extractions:', error);
    }
  }, []);

  // Load BOMs
  const loadBoms = useCallback(async () => {
    try {
      const response = await fetch('/api/bom/list');
      const data = await response.json();
      if (data.success) {
        setBoms(data.boms);
      }
    } catch (error) {
      console.error('Failed to load BOMs:', error);
    }
  }, []);

  // Load BOM details
  const loadBomDetails = useCallback(async (bomId: number) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/bom/${bomId}`);
      const data = await response.json();
      if (data.success) {
        setSelectedBom(data.bom);
        setBomItems(data.items);
      }
    } catch (error) {
      console.error('Failed to load BOM details:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Generate BOM
  const handleGenerateBom = useCallback(async () => {
    if (!selectedExtraction) return;
    
    setGenerating(true);
    try {
      const response = await fetch('/api/bom/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_id: selectedExtraction.job_id,
          name: newBomName || `BOM - ${selectedExtraction.pdf_filename}`
        })
      });
      const data = await response.json();
      if (data.success) {
        setGenerateDialogOpen(false);
        setSelectedExtraction(null);
        setNewBomName('');
        loadBoms();
        loadExtractions();
        // Load the newly created BOM
        loadBomDetails(data.bom_id);
        setActiveTab(1); // Switch to BOM view tab
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error('Failed to generate BOM:', error);
    } finally {
      setGenerating(false);
    }
  }, [selectedExtraction, newBomName, loadBoms, loadExtractions, loadBomDetails]);

  // Search ERP parts
  const searchErpParts = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setErpSearchResults([]);
      return;
    }
    try {
      const response = await fetch(`/api/erp/parts?search=${encodeURIComponent(query)}&limit=10`);
      const data = await response.json();
      if (data.success) {
        setErpSearchResults(data.parts);
      }
    } catch (error) {
      console.error('Failed to search ERP parts:', error);
    }
  }, []);

  // Update BOM item
  const updateBomItem = useCallback(async (itemId: number, updates: Partial<BOMLineItem>) => {
    if (!selectedBom) return;
    
    try {
      const response = await fetch(`/api/bom/${selectedBom.id}/item/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      const data = await response.json();
      if (data.success) {
        loadBomDetails(selectedBom.id);
      }
    } catch (error) {
      console.error('Failed to update BOM item:', error);
    }
  }, [selectedBom, loadBomDetails]);

  // Delete BOM
  const handleDeleteBom = useCallback(async (bomId: number) => {
    if (!window.confirm('Are you sure you want to delete this BOM?')) return;
    
    try {
      const response = await fetch(`/api/bom/${bomId}`, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) {
        loadBoms();
        loadExtractions();
        if (selectedBom?.id === bomId) {
          setSelectedBom(null);
          setBomItems([]);
        }
      }
    } catch (error) {
      console.error('Failed to delete BOM:', error);
    }
  }, [loadBoms, loadExtractions, selectedBom]);

  // Export to Excel
  const handleExportExcel = useCallback(async () => {
    if (!selectedBom) return;
    
    try {
      const response = await fetch(`/api/bom/${selectedBom.id}/export`);
      const data = await response.json();
      if (data.success) {
        const { export: exportData } = data;
        
        // Create workbook
        const ws = XLSX.utils.json_to_sheet(exportData.rows, {
          header: exportData.columns
        });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'BOM');
        
        // Download
        XLSX.writeFile(wb, exportData.filename);
      }
    } catch (error) {
      console.error('Failed to export BOM:', error);
    }
  }, [selectedBom]);

  // Initial load
  useEffect(() => {
    loadExtractions();
    loadBoms();
  }, [loadExtractions, loadBoms]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Bill of Materials
        </Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Available Extractions" />
          <Tab label={`Generated BOMs (${boms.length})`} />
          {selectedBom && <Tab label={`View: ${selectedBom.name}`} />}
        </Tabs>
      </Paper>

      {/* Tab 0: Extractions */}
      {activeTab === 0 && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Completed Extractions</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                size="small" 
                variant="contained"
                color="primary"
                startIcon={<UploadIcon />} 
                onClick={() => navigate('/training')}
              >
                Upload & Extract PDF
              </Button>
              <Button size="small" startIcon={<RefreshIcon />} onClick={loadExtractions}>
                Refresh
              </Button>
            </Box>
          </Box>
          
          {extractions.length === 0 ? (
            <Alert severity="info">
              No completed extractions found. Click "Upload & Extract PDF" to extract a PDF first.
            </Alert>
          ) : (
            <List>
              {extractions.map((ext, idx) => (
                <React.Fragment key={ext.job_id}>
                  <ListItem>
                    <ListItemText
                      primary={cleanFilename(ext.pdf_filename)}
                      secondary={
                        <Box component="span" sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                          <Chip label={`${ext.extraction_count} components`} size="small" />
                          <Typography variant="caption" color="text.secondary">
                            {new Date(ext.created_at).toLocaleString()}
                          </Typography>
                          {ext.has_bom && (
                            <Chip label="BOM Created" size="small" color="success" />
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      {ext.has_bom ? (
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={<ViewIcon />}
                          onClick={() => {
                            if (ext.bom_id) {
                              loadBomDetails(ext.bom_id);
                              setActiveTab(2);
                            }
                          }}
                        >
                          View BOM
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<BOMIcon />}
                          onClick={() => {
                            setSelectedExtraction(ext);
                            setNewBomName(`BOM - ${cleanFilename(ext.pdf_filename).replace('.pdf', '')}`);
                            setGenerateDialogOpen(true);
                          }}
                        >
                          Create BOM
                        </Button>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                  {idx < extractions.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>
      )}

      {/* Tab 1: BOM List */}
      {activeTab === 1 && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Generated BOMs</Typography>
            <Button size="small" startIcon={<RefreshIcon />} onClick={loadBoms}>
              Refresh
            </Button>
          </Box>
          
          {boms.length === 0 ? (
            <Alert severity="info">
              No BOMs generated yet. Select an extraction to generate a BOM.
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {boms.map((bom) => (
                <Grid item xs={12} md={6} lg={4} key={bom.id}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom noWrap>
                        {bom.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom noWrap>
                        {cleanFilename(bom.pdf_filename)}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 1, my: 1 }}>
                        <Chip label={`${bom.total_items} items`} size="small" />
                        <Chip 
                          label={`${bom.matched_items} matched`} 
                          size="small" 
                          color="success"
                          icon={<MatchedIcon />}
                        />
                        {bom.unmatched_items > 0 && (
                          <Chip 
                            label={`${bom.unmatched_items} unmatched`} 
                            size="small" 
                            color="warning"
                            icon={<UnmatchedIcon />}
                          />
                        )}
                      </Box>
                      
                      <Typography variant="caption" color="text.secondary">
                        Created: {new Date(bom.created_at).toLocaleString()}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button
                          size="small"
                          startIcon={<ViewIcon />}
                          onClick={() => {
                            loadBomDetails(bom.id);
                            setActiveTab(2);
                          }}
                        >
                          View
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteBom(bom.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      )}

      {/* Tab 2: BOM Detail View */}
      {activeTab === 2 && selectedBom && (
        <Paper sx={{ p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6">{selectedBom.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {cleanFilename(selectedBom.pdf_filename)} • {selectedBom.total_items} items
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip 
                    label={`${selectedBom.matched_items} matched`} 
                    color="success"
                    icon={<MatchedIcon />}
                  />
                  {selectedBom.unmatched_items > 0 && (
                    <Chip 
                      label={`${selectedBom.unmatched_items} unmatched`} 
                      color="warning"
                      icon={<UnmatchedIcon />}
                    />
                  )}
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportExcel}
                  >
                    Export Excel
                  </Button>
                </Box>
              </Box>

              {/* Match progress */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">ERP Match Progress</Typography>
                  <Typography variant="body2">
                    {Math.round((selectedBom.matched_items / selectedBom.total_items) * 100)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(selectedBom.matched_items / selectedBom.total_items) * 100}
                  color={selectedBom.unmatched_items === 0 ? 'success' : 'warning'}
                />
              </Box>

              <TableContainer sx={{ maxHeight: 600 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#1a1a1a', color: 'white' }}>Ref Des</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#1a1a1a', color: 'white' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'primary.main', color: 'white' }}>PP Number</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'primary.main', color: 'white' }}>Vendor</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'primary.main', color: 'white' }}>Vendor Part No.</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'primary.main', color: 'white' }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#1a1a1a', color: 'white' }}>Qty</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#1a1a1a', color: 'white' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#1a1a1a', color: 'white' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...bomItems].sort((a, b) => {
                      // Sort matched/manual first, then unmatched
                      const aMatched = a.match_status === 'matched' || a.match_status === 'manual';
                      const bMatched = b.match_status === 'matched' || b.match_status === 'manual';
                      if (aMatched && !bMatched) return -1;
                      if (!aMatched && bMatched) return 1;
                      return 0;
                    }).map((item) => (
                      <TableRow 
                        key={item.id} 
                        hover
                        sx={{
                          backgroundColor: item.match_status === 'unmatched' ? 'rgba(255, 152, 0, 0.08)' : 'inherit'
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {item.ref_des || '-'}
                          </Typography>
                          {item.page_numbers && (
                            <Typography variant="caption" color="text.secondary">
                              pg {item.page_numbers}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip label={item.component_type || 'Unknown'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                          {editingItemId === item.id ? (
                            <Autocomplete
                              size="small"
                              freeSolo
                              options={erpSearchResults}
                              getOptionLabel={(opt) => typeof opt === 'string' ? opt : `${opt.internal_part_number} - ${opt.description?.substring(0, 40) || ''}`}
                              filterOptions={(x) => x} // Disable client-side filtering, use server results
                              onInputChange={(_, value, reason) => {
                                if (reason === 'input') {
                                  setErpSearchQuery(value);
                                  searchErpParts(value);
                                }
                              }}
                              onChange={(_, part) => {
                                if (part && typeof part !== 'string') {
                                  updateBomItem(item.id, { erp_part_id: part.id });
                                }
                                setEditingItemId(null);
                              }}
                              onClose={() => setEditingItemId(null)}
                              renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                  <Box>
                                    <Typography variant="body2" fontWeight={600}>{option.internal_part_number}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {option.description?.substring(0, 50) || 'No description'}
                                    </Typography>
                                  </Box>
                                </li>
                              )}
                              noOptionsText={erpSearchQuery.length < 2 ? "Type to search..." : "No parts found"}
                              renderInput={(params) => (
                                <TextField 
                                  {...params} 
                                  placeholder="Search PP No. or description..." 
                                  autoFocus 
                                  size="small"
                                />
                              )}
                              sx={{ minWidth: 280 }}
                            />
                          ) : (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                              onClick={() => setEditingItemId(item.id)}
                            >
                              {item.pp_number || <em style={{ color: '#999' }}>Click to assign...</em>}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                          {item.vendor_name || '-'}
                        </TableCell>
                        <TableCell sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                          {item.vendor_part_number || '-'}
                        </TableCell>
                        <TableCell sx={{ backgroundColor: 'rgba(0,0,0,0.02)', maxWidth: 250 }}>
                          <Tooltip title={item.description || item.extraction_description || ''}>
                            <Typography variant="body2" noWrap>
                              {item.description || item.extraction_description || '-'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>
                          {item.match_status === 'matched' || item.match_status === 'manual' ? (
                            <Chip label="Matched" size="small" color="success" />
                          ) : (
                            <Chip label="Unmatched" size="small" color="warning" />
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton 
                            size="small"
                            onClick={() => setEditingItemId(item.id)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Paper>
      )}

      {/* Generate BOM Dialog */}
      <Dialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Bill of Materials</DialogTitle>
        <DialogContent>
          {selectedExtraction ? (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Generating BOM from: <strong>{cleanFilename(selectedExtraction.pdf_filename)}</strong>
                <br />
                Components found: {selectedExtraction.extraction_count}
              </Alert>
              <TextField
                fullWidth
                label="BOM Name"
                value={newBomName}
                onChange={(e) => setNewBomName(e.target.value)}
                placeholder="Enter a name for this BOM"
              />
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Typography gutterBottom>Select an extraction to generate a BOM:</Typography>
              <Autocomplete
                options={extractions.filter(e => !e.has_bom)}
                getOptionLabel={(opt) => `${cleanFilename(opt.pdf_filename)} (${opt.extraction_count} components)`}
                onChange={(_, ext) => {
                  setSelectedExtraction(ext);
                  if (ext) {
                    setNewBomName(`BOM - ${cleanFilename(ext.pdf_filename).replace('.pdf', '')}`);
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select extraction..." />
                )}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setGenerateDialogOpen(false);
            setSelectedExtraction(null);
            setNewBomName('');
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerateBom}
            disabled={!selectedExtraction || generating}
            startIcon={generating ? <CircularProgress size={16} /> : <BOMIcon />}
          >
            {generating ? 'Generating...' : 'Generate BOM'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BOMView;
